// UeberMich.js – Seite /ueber-mich: Vorstellung des Inhabers und des Betriebs, Link zu Instagram.
import "./Home.css";
import "./custom.css";
import usePageMeta from "../../usePageMeta";
import { Link } from "react-router-dom";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import InstagramIcon from "../InstagramIcon";
import betrieb from "../../data/betrieb";
import { JsonLd, webseiteSchema, BETRIEB_ID, INHABER_ID } from "../Strukturdaten";

import Portrait from "./images/UeberMich/portrait.webp";
import Einsatz from "./images/UeberMich/einsatz.webp";

// Seitentitel einmal definiert, damit <title> und Strukturdaten nicht auseinanderlaufen
const TITEL = `Über mich: ${betrieb.inhaber} aus ${betrieb.ort} | ${betrieb.name}`;

// Strukturdaten: Profilseite des Inhabers, verknüpft mit dem Betrieb; nur Angaben, die auf der Seite stehen
const schema = webseiteSchema({
  typ: "ProfilePage",
  pfad: "/ueber-mich",
  name: TITEL,
  mainEntity: {
    "@type": "Person",
    "@id": INHABER_ID,
    name: betrieb.inhaber,
    jobTitle: "Inhaber",
    description:
      `${betrieb.inhaber}, der Kopf hinter ${betrieb.name}: abgeschlossenes Studium in Agrarwirtschaft, Lehrgänge AS Baum 1 ` +
      "und AS Baum 2, direkter Ansprechpartner von der ersten Besichtigung bis zum Abschluss der Arbeiten.",
    worksFor: { "@id": BETRIEB_ID },
    hasCredential: [
      { "@type": "EducationalOccupationalCredential", credentialCategory: "Studienabschluss", name: "Studium Agrarwirtschaft" },
      { "@type": "EducationalOccupationalCredential", credentialCategory: "Lehrgang", name: "AS Baum 1" },
      { "@type": "EducationalOccupationalCredential", credentialCategory: "Lehrgang", name: "AS Baum 2" },
    ],
    knowsAbout: ["Baumfällung", "Baumpflege", "Landschaftspflege"],
  },
});

function UeberMich() {
  usePageMeta({
    path: "/ueber-mich",
    title: TITEL,
    description:
      "Lasse Stoltenberg, Inhaber von Forst & Flur: Agrarwirt mit AS Baum 1 und 2. Ihr direkter Ansprechpartner für Baumfällung, Baumpflege und Landschaftspflege.",
  });

  return (
    <div>
      <JsonLd daten={schema} />
      <div className="ff-page-hero">
        <div className="ff-page-hero__content">
          <span className="ff-eyebrow">Forst &amp; Flur</span>
          <h1 className="ff-page-hero__title">Über mich</h1>
          <p className="ff-page-hero__sub">Lasse Stoltenberg, der Kopf hinter Forst &amp; Flur</p>
        </div>
      </div>

      <div className="custom-dark-green text-white">
        <Container className="py-5">
          <Row className="justify-content-center align-items-start gy-4">
            <Col xs={12} md={5} lg={4}>
              <img
                src={Portrait}
                width={800}
                height={1067}
                decoding="async"
                alt="Lasse Stoltenberg in Schnittschutzkleidung mit Funkfernsteuerung und Motorsäge vor einem frisch gefällten Stammstück, dahinter die Raupe Moritz FR 75"
                className="img-fluid rounded w-100"
              />
              <p className="text-white mt-2 fs-6 fst-italic text-center">Lasse Stoltenberg nach einer Fällung, im Hintergrund die Raupe Moritz FR 75</p>
            </Col>
            <Col xs={12} md={7} lg={6}>
              <p className="fs-5 text-shadow">
                Moin, ich bin Lasse Stoltenberg, der Kopf hinter Forst &amp; Flur. Mich begeistert die
                Verbindung aus moderner Technik und der Arbeit in und mit der Natur. Genau diese
                Begeisterung hat mich dazu motiviert, meinen eigenen Betrieb aufzubauen.
              </p>
              <p className="fs-5 text-shadow">
                Mit meinem abgeschlossenen Studium in Agrarwirtschaft bringe ich fundiertes Wissen über
                landwirtschaftliche Zusammenhänge und die Bewirtschaftung von Flächen mit. Ergänzend habe
                ich die Lehrgänge AS Baum 1 und AS Baum 2 absolviert und mich damit für anspruchsvolle
                Baumarbeiten weiterqualifiziert.
              </p>
            </Col>
          </Row>

          {/* Zweite Reihe spiegelbildlich zur ersten: Text links, Einsatzfoto rechts */}
          <Row className="justify-content-center align-items-start gy-4 mt-4">
            <Col xs={12} md={7} lg={6}>
              <p className="fs-5 text-shadow">
                Was mir an meiner Arbeit besonders gefällt: Jedes Grundstück, jeder Baum und jede Fläche
                stellt uns vor andere Aufgaben. Dafür eine passende Lösung zu finden und diese mit der
                richtigen Technik umzusetzen, macht für mich den Reiz aus. Unsere funkgesteuerte Raupe
                Moritz FR 75 ist dabei eine wertvolle Unterstützung für viele Arbeiten im Forst und in der
                Landschaftspflege.
              </p>
              <p className="fs-5 text-shadow">
                Sie haben mit mir einen direkten Ansprechpartner: von der ersten Besichtigung bis zum
                Abschluss der Arbeiten. Ich lege Wert auf ehrliche Beratung, klare Absprachen und einen
                sorgfältigen Umgang mit Ihrem Grundstück. Bei der Umsetzung steht mir ein großes Netzwerk
                zur Seite, auf dessen Unterstützung ich je nach Aufgabe zurückgreifen kann.
              </p>
            </Col>
            <Col xs={12} md={5} lg={4}>
              {/* 3:4-Ausschnitt aus dem Hochformat-Original (Billder/06 Ueber mich): Raupe, Mulcher und Funkfernsteuerung ganz im Bild */}
              <img
                src={Einsatz}
                width={900}
                height={1200}
                loading="lazy"
                decoding="async"
                alt="Lasse Stoltenberg mit Gehörschutz und Funkfernsteuerung neben der Raupe Moritz FR 75 mit angebautem Schlegelmulcher"
                className="img-fluid rounded w-100"
              />
              <p className="text-white mt-2 fs-6 fst-italic text-center">
                Kurze Pause beim Mulchen: die Raupe Moritz FR 75 mit Schlegelmulcher
              </p>
            </Col>
          </Row>

          <Row className="justify-content-center mt-4">
            <Col xs={12} lg={10}>
              <p className="fs-5 text-shadow">
                Ob Baumfällung, Baumpflege oder Landschaftspflege: Ich freue mich darauf, Ihr Vorhaben in
                Schleswig-Holstein gemeinsam mit Ihnen anzupacken.
              </p>

              <div className="mt-4">
                <Link to="/kontakt" className="btn-ff-primary" onClick={() => window.scrollTo(0, 0)}>
                  Jetzt anfragen
                </Link>
                <a
                  href={betrieb.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ff-outline ms-3"
                >
                  <InstagramIcon className="me-2" />Folgen Sie uns auf Instagram
                </a>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
}

export default UeberMich;
